import { createEventType } from "@remix-run/events";

export type Cell = number | null;
export type Board = Cell[][];
export type Direction = "up" | "down" | "left" | "right";

let [move, createMove] = createEventType<{ direction: Direction }>("2048:move");
let [win, createWin] = createEventType("2048:win");
let [gameOver, createGameOver] = createEventType("2048:game-over");
let [reset, createReset] = createEventType("2048:reset");
let [change, createChange] = createEventType("2048:change");

export class Game2048 extends EventTarget {
  #board: Board;
  #score = 0;
  #isWon = false;
  #isGameOver = false;
  #size = 4;

  static move = move;
  static win = win;
  static gameOver = gameOver;
  static reset = reset;
  static change = change;

  constructor() {
    super();
    this.#board = this.createEmptyBoard();
    this.addRandomTile();
    this.addRandomTile();
  }

  get board(): Board {
    return this.#board.map(row => [...row]);
  }

  get score(): number {
    return this.#score;
  }

  get isWon(): boolean {
    return this.#isWon;
  }

  get isGameOver(): boolean {
    return this.#isGameOver;
  }

  get size(): number {
    return this.#size;
  }

  private createEmptyBoard(): Board {
    return Array.from({ length: this.#size }, () => 
      Array.from({ length: this.#size }, () => null)
    );
  }

  private addRandomTile(): boolean {
    const emptyCells: [number, number][] = [];
    
    for (let row = 0; row < this.#size; row++) {
      for (let col = 0; col < this.#size; col++) {
        if (this.#board[row][col] === null) {
          emptyCells.push([row, col]);
        }
      }
    }

    if (emptyCells.length === 0) {
      return false;
    }

    const [row, col] = emptyCells[Math.floor(Math.random() * emptyCells.length)];
    this.#board[row][col] = Math.random() < 0.9 ? 2 : 4;
    return true;
  }

  makeMove(direction: Direction): boolean {
    if (this.#isGameOver) return false;

    const oldBoard = JSON.stringify(this.#board);
    
    switch (direction) {
      case "left":
        this.moveLeft();
        break;
      case "right":
        this.moveRight();
        break;
      case "up":
        this.moveUp();
        break;
      case "down":
        this.moveDown();
        break;
    }

    // Check if board changed
    if (oldBoard !== JSON.stringify(this.#board)) {
      this.addRandomTile();
      this.dispatchEvent(createMove({ detail: { direction } }));
      
      if (this.checkWin()) {
        this.#isWon = true;
        this.dispatchEvent(createWin());
      } else if (this.checkGameOver()) {
        this.#isGameOver = true;
        this.dispatchEvent(createGameOver());
      }
      
      this.dispatchEvent(createChange());
      return true;
    }

    return false;
  }

  private moveLeft(): void {
    for (let row = 0; row < this.#size; row++) {
      const newRow = this.mergeTiles(this.#board[row]);
      this.#board[row] = newRow;
    }
  }

  private moveRight(): void {
    for (let row = 0; row < this.#size; row++) {
      const reversed = [...this.#board[row]].reverse();
      const merged = this.mergeTiles(reversed);
      this.#board[row] = merged.reverse();
    }
  }

  private moveUp(): void {
    for (let col = 0; col < this.#size; col++) {
      const column = this.#board.map(row => row[col]);
      const merged = this.mergeTiles(column);
      for (let row = 0; row < this.#size; row++) {
        this.#board[row][col] = merged[row];
      }
    }
  }

  private moveDown(): void {
    for (let col = 0; col < this.#size; col++) {
      const column = this.#board.map(row => row[col]).reverse();
      const merged = this.mergeTiles(column).reverse();
      for (let row = 0; row < this.#size; row++) {
        this.#board[row][col] = merged[row];
      }
    }
  }

  private mergeTiles(tiles: Cell[]): Cell[] {
    // Remove nulls and move tiles to the left
    const filtered = tiles.filter(tile => tile !== null);
    const merged: Cell[] = [];
    
    let i = 0;
    while (i < filtered.length) {
      if (i + 1 < filtered.length && filtered[i] === filtered[i + 1]) {
        // Merge tiles
        const value = filtered[i]! * 2;
        merged.push(value);
        this.#score += value;
        i += 2;
      } else {
        merged.push(filtered[i]);
        i += 1;
      }
    }
    
    // Fill the rest with nulls
    while (merged.length < this.#size) {
      merged.push(null);
    }
    
    return merged;
  }

  private checkWin(): boolean {
    if (this.#isWon) return false;
    
    for (let row = 0; row < this.#size; row++) {
      for (let col = 0; col < this.#size; col++) {
        if (this.#board[row][col] === 2048) {
          return true;
        }
      }
    }
    return false;
  }

  private checkGameOver(): boolean {
    // Check if there are any empty cells
    for (let row = 0; row < this.#size; row++) {
      for (let col = 0; col < this.#size; col++) {
        if (this.#board[row][col] === null) {
          return false;
        }
      }
    }

    // Check if any adjacent cells can be merged
    for (let row = 0; row < this.#size; row++) {
      for (let col = 0; col < this.#size; col++) {
        const current = this.#board[row][col];
        
        // Check right
        if (col < this.#size - 1 && this.#board[row][col + 1] === current) {
          return false;
        }
        
        // Check down
        if (row < this.#size - 1 && this.#board[row + 1][col] === current) {
          return false;
        }
      }
    }

    return true;
  }

  resetGame(): void {
    this.#board = this.createEmptyBoard();
    this.#score = 0;
    this.#isWon = false;
    this.#isGameOver = false;
    this.addRandomTile();
    this.addRandomTile();
    this.dispatchEvent(createReset());
    this.dispatchEvent(createChange());
  }

  continueGame(): void {
    if (this.#isWon) {
      this.#isWon = false;
      this.dispatchEvent(createChange());
    }
  }
}

